import classNames from 'classnames';
import truncate from 'lodash/truncate';
import { type ComponentProps, useEffect } from 'react';

import { Button, Popover, useDisclosure } from '@revelio/ui';

import { PopoutTree } from '../../../../shared/components/tree/PopoutTree/PopoutTree';
import { useTreeSelections } from '../../../../shared/components/tree/use-tree-selections';
import { SelectionListControls } from '../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import type { SubfilterTree } from '../PageState/types';
import styles from './Subfilter.module.css';

const SubfilterChevron = () => (
  <svg
    width="6"
    height="4"
    viewBox="0 0 6 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g opacity="0.4">
      <path
        d="M5.5 0.5L3 3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.5 0.5L2.9 2.95"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const Subfilter = ({
  subfilterList,
  selectedItems,
  onSubmit,
  openByDefault,
  className,
  ...props
}: {
  subfilterList: SubfilterTree[];
  selectedItems: SubfilterTree[];
  openByDefault?: boolean;
  onSubmit: (selections: SubfilterTree[]) => void;
} & Omit<ComponentProps<typeof Button>, 'onSubmit'>) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const label = (() => {
    if (selectedItems.length === 0) return 'Select a Segment';
    if (selectedItems.length === 1) {
      return truncate(selectedItems[0]?.name ?? '', {
        length: 20,
        omission: '...',
      });
    }

    return `${selectedItems.length} selected`;
  })();

  return (
    <Popover
      placement="bottom"
      open={isOpen}
      onOpenChange={(next) => (next ? onOpen() : onClose())}
      className={styles.popoverContent}
      trigger={
        <Button
          data-testid="plot-sub-filter-trigger"
          className={classNames(styles.trigger, className)}
          variant="outlined"
          aria-label="Plot Sub-filter"
          size="xs"
          rightIcon={<SubfilterChevron />}
          {...props}
        >
          {label}
        </Button>
      }
    >
      <div className={styles.popoverBody}>
        <SubfilterTreeSelection
          data={subfilterList}
          selectedItems={selectedItems}
          onClose={onClose}
          onSubmit={onSubmit}
          openByDefault={openByDefault}
        />
      </div>
    </Popover>
  );
};

const SubfilterTreeSelection = ({
  data,
  selectedItems,
  onClose,
  onSubmit,
  openByDefault,
}: {
  data: SubfilterTree[];
  selectedItems: SubfilterTree[];
  onClose?: () => void;
  onSubmit: (selections: SubfilterTree[]) => void;
  openByDefault?: boolean;
}) => {
  const { selections, onChange, onClear } = useTreeSelections({
    initialSelections: selectedItems.map((item) => item.id),
  });

  const hasTreeStructure = data.some(
    (item) => item.children && item.children.length > 0
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (selectedItems.every((item) => selections.includes(item.id))) return;
    onChange(selectedItems.map((item) => item.id));
  }, [selectedItems]);

  const handleSubmit = () => {
    if (hasTreeStructure) {
      const flatData = data.flatMap((item) => [item, ...(item.children ?? [])]);
      const selectionItems = flatData.filter((item) =>
        selections.includes(item.id)
      );
      onSubmit(selectionItems);
      onClose?.();
    } else {
      const selectionItems = data.filter((item) =>
        selections.includes(item.id)
      );
      onSubmit(selectionItems);
      onClose?.();
    }
  };

  return (
    <div data-testid="plot-sub-filter-tree">
      <PopoutTree
        data={data}
        value={selections}
        onChange={onChange}
        openByDefault={openByDefault}
        skipDefaultSort={!hasTreeStructure}
        showCollapseToggle={false}
        {...(openByDefault && { disabledLevels: [0] })}
      />
      <SelectionListControls
        className={styles.selectionListControls}
        onClear={onClear}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitText="Apply"
      />
    </div>
  );
};
